import jsonSchemaTraverse from 'json-schema-traverse';
import jsonPointer from 'json-pointer';
import isEqual from 'react-fast-compare';
import isPlainObject from 'lodash/isPlainObject';
import isUndefined from 'lodash/isUndefined';
import isFunction from 'lodash/isFunction';
import assign from 'assign-deep';
import set from 'set-value';
import copy from 'fast-copy';
import toDataPath from './toDataPath';
import { hasRestrictedPathSegment } from './restrictedPathSegment';
import mergeDeep from './mergeDeep';
import getPointer from './getPointer';
import stringToBoolean from './stringToBoolean';
import getDict from './getDict';
import dictToObject from './dictToObject';
import toString from './toString';
import isObjectLike from './isObjectLike';

const URI_POINTER = /^(.+#)(?:\/(?:[a-z0-9_\-.!$&'()*+,;:=@]|%[0-9a-f]{2}|~0|~1)*)*$/i;
const ROOT_REF = /(["']{0,1}\$ref["']{0,1}:\s*?["']{1})(#?)((?:\/(?:[a-z0-9_\-.!$&'()*+,;:=@]|%[0-9a-f]{2}|~0|~1)*)+["']{1})/gi;

// TODO: Find out if more null values need to be removed from schema
const removeNullForKey = (key) => ['$ref'].includes(key);

const removeNull = (schema) => {
  for (const [key, value] of Object.entries(schema)) {
    if (value == null && removeNullForKey(key)) {
      delete schema[key]; // eslint-disable-line no-param-reassign
    }

    if (isPlainObject(value)) {
      removeNull(value);
    }
  }
};

const patch = (object, path, value) => {
  if (path.length === 0) {
    Object.assign(object, value);
  } else {
    set(object, path.join('.'), value);
  }
};

const buildSchemaMap = (schemaMap) => (schema, pointer, root, parentPointer, parentKeyword) => {
  if (pointer === '' || parentKeyword === 'properties') {
    schemaMap.set(pointer, schema);
  }
};

const injectRequired = (
  schema,
  pointer,
  root,
  parentPointer,
  parentKeyword,
  parentSchema,
  propertyName
) => {
  if (Array.isArray(parentSchema?.required) && parentSchema.required.includes(propertyName)) {
    // eslint-disable-next-line no-param-reassign
    schema['ui:required'] = true;
  }
};

// TODO: Look for possibilities of caching heavy computations
const buildSchemaDetails = ({
  adapter,
  defaultValues,
  ifThenElseConditions,
  references,
  touchedValues,
  updatedValues,
  validator,
  values,
}) => (schema, pointer, root, parentPointer, parentKeyword, parentSchema) => {
  const dataPath = toDataPath(pointer);
  const { $ref, type, format, oneOf, enum: enumerable } = schema;
  let defaultValue = !isUndefined(schema.default) ? schema.default : defaultValues[dataPath];
  const currentValue = getPointer(values, dataPath);
  const hasOptions = Array.isArray(oneOf) || Array.isArray(enumerable);
  const control = schema['ui:props']?.control ?? schema['ui:control'] ?? schema.type;

  if ($ref) {
    let refSchema;

    const refUriMatch = $ref.match(URI_POINTER);
    const refUriSchema =
      refUriMatch != null && validator && isFunction(validator.getSchema)
        ? validator.getSchema(refUriMatch[1])
        : null;

    if (refUriSchema) {
      refSchema = getPointer(refUriSchema.schema, $ref.replace(refUriMatch[1], ''));

      // keep references to own root schema
      try {
        refSchema = JSON.parse(
          JSON.stringify(refSchema).replace(
            ROOT_REF,
            (_, prefix, rootPath, suffix) => `${prefix}${refUriSchema.schema.$id}#${suffix}`
          )
        );
      } catch (e) {
        refSchema = null;
      }
    } else {
      refSchema = getPointer(root, $ref);
    }

    if (refSchema) {
      references.set(pointer, {
        ...refSchema,
        $ref,
      });
    }
  }

  // parse if/then/else condition
  // TODO: Cache computation of if/then/else??
  if (parentKeyword === 'if') {
    const segments = jsonPointer.parse(parentPointer);
    const allOfIndex = segments.lastIndexOf('allOf');
    const parentSegments = allOfIndex > -1 ? segments.slice(0, allOfIndex) : segments;

    if (!hasRestrictedPathSegment(parentSegments)) {
      ifThenElseConditions.set(
        {
          parentPointer: jsonPointer.compile(parentSegments),
          condition: {
            type: parentSchema?.type,
            ...schema,
          },
        },
        {
          then: parentSchema.then,
          otherwise: parentSchema.else,
        }
      );
    }
  }

  // set empty object as default value when the object is required
  // and the object has required properties of its own
  // NOTE: used to check if object was required on parent schema
  const { required = [] } = schema;
  if ((type === 'object' || isObjectLike(schema)) && required.length > 0) {
    defaultValue = {};
  }

  // parse value for boolean type
  if (type === 'boolean') {
    defaultValue = !isUndefined(defaultValue) ? stringToBoolean(defaultValue) : false;
  }

  // parse default value for color format
  if (format === 'color') {
    defaultValue = defaultValue ?? '#000000';
  }

  // parse default enum value
  if (type !== 'array' && parentKeyword !== 'items' && hasOptions) {
    const segments = jsonPointer.parse(pointer);

    if (!hasRestrictedPathSegment(segments)) {
      let isCurrentValueValid = false;
      let optionValues;

      if (Array.isArray(enumerable)) {
        optionValues = enumerable;
      } else if (Array.isArray(oneOf)) {
        optionValues = oneOf.map(({ const: value }) => value);
      }

      let [schemaDefaultValue] = optionValues;

      if (defaultValue === '' || defaultValue === null) {
        schemaDefaultValue = undefined;
      }

      isCurrentValueValid = optionValues
        .map((value) => toString(value))
        .includes(toString(currentValue));
      defaultValue =
        !isUndefined(defaultValue) && optionValues.includes(defaultValue)
          ? defaultValue
          : schemaDefaultValue;

      if (!isUndefined(currentValue) && !isCurrentValueValid) {
        delete updatedValues[dataPath]; // eslint-disable-line no-param-reassign
      }
    }
  }

  // parse default range value
  if (type === 'number' && schema['ui:control'] === 'range') {
    const { maximum: max = 100, minimum: min = 0 } = schema;

    defaultValue = Math.round((max + min) / 2);
  }

  const { defaultValue: adapterDefaultValue } = adapter?.findComponent(control, hasOptions) ?? {};

  // parse default value
  if (!isUndefined(defaultValue) || !isUndefined(adapterDefaultValue)) {
    const segments = jsonPointer.parse(pointer);

    if (!touchedValues.has(dataPath) && !hasRestrictedPathSegment(segments)) {
      // eslint-disable-next-line no-param-reassign
      defaultValues[dataPath] = !isUndefined(defaultValue) ? defaultValue : adapterDefaultValue;
    }
  }
};

const traverse = ({ adapter, formDefaultValues, schema, touchedValues, validator, values }) => {
  // eslint-disable-next-line no-void
  const defaultValues = formDefaultValues;
  const ifThenElseConditions = new Map();
  const updatedValues = getDict(values, false);
  const references = new Map();

  jsonSchemaTraverse(schema, {
    allKeys: true,
    cb: buildSchemaDetails({
      adapter,
      defaultValues,
      ifThenElseConditions,
      references,
      touchedValues,
      updatedValues,
      validator,
      values,
    }),
  });

  return {
    references,
    defaultValues,
    updatedValues,
    ifThenElseConditions,
  };
};

const compile = ({ ifThenElseConditions, references, schema, schemaMap, validator, values }) => {
  let compiledSchema = copy(schema);

  for (const [pointer, refSchema] of references.entries()) {
    const schemaUpdate = {};
    const segments = jsonPointer.parse(pointer);
    const isAllOf = segments.slice(0, -1).pop() === 'allOf';

    // support nested allOf when using $ref inside of allOf
    if (isAllOf) {
      const allOfPointer = jsonPointer.compile(segments.slice(0, -1));
      const compiledSchemaAllOf = getPointer(compiledSchema, allOfPointer, []).slice();
      const allOfIndex = compiledSchemaAllOf.findIndex(({ $ref }) => $ref === refSchema.$ref);

      refSchema.allOf = refSchema.allOf || [];
      refSchema.$ref = null; // do not leak $ref after this point
      compiledSchemaAllOf.splice(allOfIndex, 1, ...refSchema.allOf.slice());

      patch(schemaUpdate, [], {
        ...refSchema,
        allOf: compiledSchemaAllOf,
      });
    } else if (!hasRestrictedPathSegment(segments)) {
      refSchema.$ref = null; // do not leak $ref after this point
      const { allOff, ...schemaWithoutAllOf } = refSchema; // drop allOf from refSchema
      patch(schemaUpdate, segments, schemaWithoutAllOf);
    }

    // eslint-disable-next-line consistent-return
    compiledSchema = mergeDeep(compiledSchema, schemaUpdate, { excludeKeys: ['required'] });
  }

  for (const [
    { parentPointer, condition },
    { then, otherwise },
  ] of ifThenElseConditions.entries()) {
    const schemaUpdate = {};
    const dataPath = toDataPath(parentPointer);
    const segments = jsonPointer.parse(parentPointer);
    const data = getPointer(values, dataPath, null);
    const isConditionMet =
      validator && isFunction(validator.validate) ? validator.validate(condition, data) : false;

    if (isConditionMet && then) {
      patch(schemaUpdate, segments, then);
    } else if (!isConditionMet && otherwise) {
      patch(schemaUpdate, segments, otherwise);
    }

    // eslint-disable-next-line consistent-return
    compiledSchema = mergeDeep(compiledSchema, schemaUpdate, { excludeKeys: ['required'] });
  }

  jsonSchemaTraverse(compiledSchema, {
    allKeys: true,
    cb: {
      pre: injectRequired,
      post: buildSchemaMap(schemaMap),
    },
  });

  return compiledSchema;
};

const getCompiledSchema = ({
  adapter,
  defaultValues: formDefaultValues = { '': void 0 }, // eslint-disable-line no-void
  schema,
  shouldUpdateValues = true,
  touchedValues = new Set(),
  validator,
  values,
}) => {
  removeNull(schema); // values cannot be null in schema

  const { defaultValues, ifThenElseConditions, references, updatedValues } = traverse({
    adapter,
    formDefaultValues,
    schema,
    touchedValues,
    validator,
    values: dictToObject(values),
  });
  let compiledValues = values;

  if (shouldUpdateValues) {
    compiledValues = assign(
      {},
      defaultValues, // clone in order not to overwrite any values
      updatedValues
    );
  }

  const schemaMap = new Map();

  const compiledSchema = compile({
    ifThenElseConditions,
    references,
    schema,
    schemaMap,
    validator,
    values: dictToObject(compiledValues),
  });

  if (!isEqual(schema, compiledSchema)) {
    return getCompiledSchema({
      defaultValues,
      schema: compiledSchema,
      shouldUpdateValues,
      touchedValues,
      validator,
      values: compiledValues,
    });
  }

  const returnValue = {
    schema: compiledSchema,
    defaultValues: shouldUpdateValues ? defaultValues : {},
    values: compiledValues,
    schemaMap,
  };

  if (validator && isFunction(validator.onAfterSchemaCompilation)) {
    return validator.onAfterSchemaCompilation(returnValue);
  }

  return returnValue;
};

export default getCompiledSchema;
